import React, { useEffect, useId, useRef, useState } from 'react';
import { BTN } from '../constants/buttons';
import { FIELD_LABEL_CLASS } from '../constants/inputs';
import { MODAL_OVERLAY, MODAL_PANEL_MOBILE_CAP } from '../constants/overlayLayers';

// Below Tailwind's `md` (768px) — the exact complement of `md:`'s
// `min-width: 768px` (the .98 is load-bearing; see `gags/GagGroupTable.js`'s
// NARROW_QUERY). Drives the `…Mobile` labels only.
const MOBILE_QUERY = '(max-width: 767.98px)';
const matchesMobile = () =>
  typeof window !== 'undefined' && typeof window.matchMedia === 'function'
  && window.matchMedia(MOBILE_QUERY).matches;

// Elements that can receive keyboard focus inside the panel (disabled ones are
// excluded so the trap skips a gated/busy confirm button correctly).
const FOCUSABLE =
  'a[href], button:not([disabled]), textarea:not([disabled]), ' +
  'input:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

/**
 * Shared, controlled confirmation dialog — the app's replacement for native
 * `window.confirm` (and `window.prompt` when a reason is needed). Modal shell
 * (backdrop / overlay-close / Escape / autofocus) mirrors GagCreateModal; the
 * danger + type-to-confirm pattern mirrors the PeopleManager delete modal.
 *
 * A11y: focus is trapped within the panel (Tab / Shift+Tab wrap at the ends),
 * captured on open and returned to the trigger on close, and the dialog is
 * labelled via aria-labelledby (title) / aria-describedby (message).
 *
 * Props:
 *   open          — render nothing unless true.
 *   title         — heading (red in the danger variant); string or node.
 *   message       — body copy (string or node).
 *   confirmLabel  — confirm button text (default 'Confirm').
 *   cancelLabel   — cancel button text (default 'Cancel').
 *   variant       — 'default' | 'danger' | 'save' (danger → red title +
 *                   BTN.danger; save → BTN.confirm, the green Save/commit
 *                   intent — owner ruling 2026-09-25: a Save is green, a
 *                   start/create/navigate is blue. OPT-IN: pass it only when
 *                   the confirm action PERSISTS the user's edit).
 *   requireText   — when set, render a type-to-confirm input; the confirm
 *                   button stays disabled until the typed value matches exactly.
 *   promptLabel   — when set, render a controlled <textarea> for reason capture;
 *                   value is `promptValue`, edits go to `onPromptChange`. The
 *                   parent reads promptValue in its onConfirm handler.
 *   promptValue   — controlled textarea value.
 *   onPromptChange(value) — textarea change handler.
 *   promptMaxLength — OPTIONAL `maxLength` on that textarea (2026-09-13, for
 *                   the post-approval pause's reason cap). Omitted → no
 *                   attribute at all, so every existing caller is unchanged.
 *   busy          — disable every button + show a working label on confirm.
 *   onConfirm     — called when the confirm button is clicked.
 *   onCancel      — called on cancel / Escape / overlay click (guarded by !busy).
 *   altLabel      — OPTIONAL third button, rendered BETWEEN cancel and confirm.
 *   onAlt         — its handler. Omit either and no third button exists.
 *   altVariant    — OPTIONAL `BTN` intent for the alt button (default
 *                   `'danger-soft'`, the value this button was hard-coded to
 *                   before 2026-09-02). Added for a COMMITTING, non-destructive
 *                   alt action ("Extend & view order") that cannot honestly
 *                   wear the destructive class; every existing caller omits it
 *                   and is byte-identical.
 *   confirmVariant — OPTIONAL `BTN` intent for the CONFIRM button alone
 *                   (POL2 fix round 1, 2026-09-25). `variant` still owns the
 *                   title; this overrides only the button colour — a closed
 *                   hire's money warning keeps a red title with a blue
 *                   confirm. Omitted, the variant decides, as before.
 *   initialFocus  — 'confirm' (default) | 'cancel'. Which button takes focus on
 *                   open, when there is no require-text input, prompt textarea
 *                   or `children` body to claim it first.
 *   children      — OPTIONAL body slot, rendered after `message` / `requireText`
 *                   / `promptLabel` and before the buttons. See below.
 *   confirmDisabled — OPTIONAL extra disable condition on the confirm button,
 *                   OR'd with `busy` and the require-text gate. See below.
 *   hideCancel    — OPTIONAL (default false). Renders NO cancel button, for a
 *                   blocking NOTICE with one acknowledgement button (added
 *                   2026-09-28 for the "untick delivered first" notice in
 *                   `OrderForm`). Escape / overlay still call `onCancel`, so
 *                   pass the same handler to both. Omitted → byte-identical.
 *
 * ── 🔀 THE BODY SLOT (2026-08-11) — WHY IT IS HERE AND NOT A FIFTH MODAL ──
 * Added for `components/NotifyUserDialog.js`, whose body is a multi-select over
 * `constants/notifyReasons.js` plus a counted free-text note. That is not a
 * confirm dialog's vocabulary and **must not become one** — the alternative
 * considered and rejected was `reasons` / `onReasonsChange` / `noteMaxLength`
 * props here, which is one feature's model wearing a shared primitive's name,
 * the failure `ui/PageHeaderBand`'s docblock records ("a shared component with
 * a `count` or `isAdmin` prop is four pages' logic wearing one component's
 * name").
 *
 * 🔑 **THE REASON TO EXTEND RATHER THAN HAND-ROLL IS THE FOUR THINGS THIS FILE
 * ALREADY GETS RIGHT**: the focus trap that wraps at both ends, Escape,
 * overlay-`mousedown` close, and focus RETURN to the trigger. A fifth
 * hand-rolled modal in this repo would get some subset of them subtly wrong,
 * and the docblock above already says so in terms.
 *
 * 🔑 **BOTH PROPS ARE OPTIONAL AND DEFAULT TO TODAY'S BEHAVIOUR.** With no
 * `children` nothing extra renders — not an empty wrapper that still eats a
 * margin. With `confirmDisabled` left alone the confirm gate is exactly
 * `busy || gateBlocked`, character for character what it was. Every caller
 * live when these landed is byte-identical; pinned by `ConfirmDialog.test.js`.
 *
 * ⚠️ **THE AUTOFOCUS PRIORITY GAINED A FOURTH RUNG AND IT IS NOT COSMETIC.**
 * A dialog whose confirm button starts DISABLED (which a validating body slot
 * routinely produces) cannot focus it — `focus()` on a disabled button is a
 * no-op, so focus would rest on `<body>`, outside the panel, and the first Tab
 * would walk the page behind the overlay rather than the dialog. The slot's
 * first focusable element therefore claims focus ahead of either button. This
 * is the SAME reasoning the require-text and prompt rungs already carry —
 * *"a dialog that asks you to TYPE something should focus what you type into"*
 * — extended to choosing.
 *
 * ── 🔀 THE THIRD BUTTON (2026-08-08) — WHY IT IS HERE AND NOT HAND-ROLLED ──
 * Added for the order-list inline editor's save/discard-on-collapse prompt,
 * which needs three outcomes: `Keep editing` · `Discard changes` · `Save and
 * close`. That is a MODAL with a focus trap, an overlay, Escape handling and
 * focus return — every one of which this component already gets right and a
 * fourth hand-rolled modal in this repo would get subtly wrong.
 *
 * 🔑 **BOTH NEW PROPS ARE OPTIONAL AND DEFAULT TO TODAY'S BEHAVIOUR, SO EVERY
 * EXISTING CALLER IS BYTE-IDENTICAL.** With no `altLabel`/`onAlt` no third
 * button renders at all — not a hidden one, not an empty flex child that still
 * eats the `gap-2`. With `initialFocus` left alone it is `'confirm'`, which is
 * exactly the autofocus priority this file already had. The five callers live
 * WHEN THE PROPS LANDED (`GagDetailPanel`, `hires/GanttView`,
 * `teams/CrossTeamGanttView`, `TeamManage`, `OrderInlineEditor`) pass neither
 * and are unaffected; pinned by `ConfirmDialog.test.js`.
 *
 * ⚠️ **THAT IS A STATEMENT ABOUT THE EXTENSION, NOT A CENSUS — corrected
 * 2026-08-09, when it acquired a SIXTH caller that DOES pass all three.**
 * `components/gags/GagGroupTable.js` uses them for the Gag Breakdown's inline
 * row editor's collapse prompt, with the same labels and the same
 * `initialFocus: 'cancel'`. **Count the callers, never quote a number from
 * here** — nothing turns red when this line drifts, and it drifted within a day.
 *
 * 📱 **`altLabelMobile` / `confirmLabelMobile` (2026-10-01)** — optional
 * SHORT labels rendered below `md` (768px) in place of `altLabel` /
 * `confirmLabel`. Passing either also keeps the button row on ONE line at
 * every width (`flex-nowrap`), so pass them only with labels short enough to
 * fit a 360px phone. The unsaved-changes prompt is the one caller and spreads
 * them from `constants/unsavedChangesPrompt.js`. Omitted, nothing changes: no
 * media listener, and the pre-existing class strings byte for byte. jsdom has
 * no `matchMedia`, so a test sees the full labels unless it stubs one.
 *
 * ⚠️ **ORDER IS A DESIGN DECISION, NOT AN ACCIDENT OF THE JSX.** Left to right:
 * cancel · alt · confirm. The consumer's labels then read as an escalation from
 * "change nothing" to "lose work" to "commit work", each saying what it DOES —
 * and the destructive middle option is the one the cursor is least likely to
 * land on by momentum, unlike a rightmost destructive button next to the
 * primary. **Do not reorder to put alt last** to match some other dialog.
 */
export default function ConfirmDialog({
  open,
  title,
  message,
  confirmLabel = 'Confirm',
  cancelLabel = 'Cancel',
  altLabel,
  onAlt,
  altVariant = 'danger-soft',
  altLabelMobile,
  confirmLabelMobile,
  confirmVariant,
  variant = 'default',
  requireText,
  promptLabel,
  promptValue = '',
  onPromptChange,
  promptMaxLength,
  busy = false,
  initialFocus = 'confirm',
  onConfirm,
  onCancel,
  children,
  confirmDisabled = false,
  hideCancel = false,
}) {
  // Type-to-confirm gate is self-contained (resets each open); the reason
  // textarea, by contrast, is parent-controlled via promptValue.
  const [typed, setTyped] = useState('');
  const panelRef = useRef(null);
  const confirmRef = useRef(null);
  const cancelRef = useRef(null);
  const requireRef = useRef(null);
  const promptRef = useRef(null);
  const bodyRef = useRef(null);

  const baseId = useId();
  const titleId = `${baseId}-title`;
  const descId = `${baseId}-desc`;

  const hasRequireText = requireText != null && requireText !== '';
  const hasPrompt = promptLabel != null;
  // BOTH halves required: a label with no handler is a button that does nothing,
  // and a handler with no label is unreachable. Either alone renders nothing.
  const hasAlt = altLabel != null && altLabel !== '' && typeof onAlt === 'function';
  const hasBody = children != null && children !== false;
  const hasMobileLabels = !!(altLabelMobile || confirmLabelMobile);

  const [isMobile, setIsMobile] = useState(() => hasMobileLabels && matchesMobile());
  useEffect(() => {
    if (!open || !hasMobileLabels || typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
      return undefined;
    }
    const mq = window.matchMedia(MOBILE_QUERY);
    setIsMobile(mq.matches);
    const onChange = (e) => setIsMobile(e.matches);
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, [open, hasMobileLabels]);
  const shownAltLabel = isMobile && altLabelMobile ? altLabelMobile : altLabel;
  const shownConfirmLabel = isMobile && confirmLabelMobile ? confirmLabelMobile : confirmLabel;
  // Below `md` the short-label row tightens each button to `px-3`, so the
  // in-flight `Working…` still fits one row at 360px (measured: with `px-4` it
  // spilled 20px into the panel padding). From `md` up it is the BTN class.
  const rowBtnCls = (cls) => (hasMobileLabels ? `${cls} max-md:px-3` : cls);

  // Reset the gate whenever the dialog (re)opens.
  useEffect(() => {
    if (open) setTyped('');
  }, [open]);

  // Capture the trigger on open; return focus to it on close/unmount (but not
  // if it has since left the document / lost its focus method).
  useEffect(() => {
    if (!open) return undefined;
    const previouslyFocused = document.activeElement;
    return () => {
      if (
        previouslyFocused &&
        typeof previouslyFocused.focus === 'function' &&
        document.contains(previouslyFocused)
      ) {
        previouslyFocused.focus();
      }
    };
  }, [open]);

  // Autofocus priority: type-to-confirm input → reason textarea → the button
  // named by `initialFocus` (default confirm, i.e. the pre-2026-08-08 behaviour).
  //
  // 🔑 `initialFocus: 'cancel'` exists so a dialog whose SAFE outcome is the
  // cancel slot can put the keyboard on it. Where a dialog can lose work, the
  // button under an immediate Enter must be the always-recoverable one — the
  // save/discard-on-collapse prompt focuses `Keep editing` for exactly that
  // reason, so Enter can never discard an edit and can never fire an
  // unreviewed save. The require-text and prompt cases still win, because a
  // dialog that asks you to TYPE something should focus what you type into.
  //
  // 🔑 THE `children` RUNG (2026-08-11) sits below those two and above both
  // buttons, and it exists because a validating body slot routinely opens with
  // the confirm button DISABLED — `focus()` on a disabled button is a no-op, so
  // without this rung focus rests on `<body>`, OUTSIDE the panel, and the first
  // Tab walks the page behind the overlay instead of the dialog. Falls through
  // to the buttons when the slot holds nothing focusable, so a purely
  // presentational body changes nothing.
  useEffect(() => {
    if (!open) return undefined;
    const t = setTimeout(() => {
      if (hasRequireText) requireRef.current?.focus();
      else if (hasPrompt) promptRef.current?.focus();
      else if (hasBody && bodyRef.current?.querySelector(FOCUSABLE)) {
        bodyRef.current.querySelector(FOCUSABLE).focus();
      } else if (initialFocus === 'cancel') cancelRef.current?.focus();
      else confirmRef.current?.focus();
    }, 0);
    return () => clearTimeout(t);
  }, [open, hasRequireText, hasPrompt, hasBody, initialFocus]);

  // Escape closes (never mid-submit); Tab / Shift+Tab are trapped in the panel.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === 'Escape') {
        if (!busy) onCancel?.();
        return;
      }
      if (e.key !== 'Tab') return;
      const panel = panelRef.current;
      if (!panel) return;
      const items = Array.from(panel.querySelectorAll(FOCUSABLE));
      // While `busy`, every control is disabled so the panel has no focusable
      // target — the trap intentionally no-ops (focus may rest outside for the
      // duration of the in-flight submit; an accepted transient gap).
      if (items.length === 0) return;
      const first = items[0];
      const last = items[items.length - 1];
      const active = document.activeElement;
      if (e.shiftKey) {
        if (active === first || !panel.contains(active)) {
          e.preventDefault();
          last.focus();
        }
      } else if (active === last || !panel.contains(active)) {
        e.preventDefault();
        first.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, busy, onCancel]);

  if (!open) return null;

  const isDanger = variant === 'danger';
  const gateBlocked = hasRequireText && typed !== requireText;
  // `confirmDisabled` is an EXTRA condition, OR'd in — it can only ever
  // disable, never re-enable something `busy` or the type-to-confirm gate has
  // already refused. Defaulting it `false` is what keeps the expression
  // character-for-character what it was for every caller that omits it.
  const isConfirmDisabled = busy || gateBlocked || confirmDisabled === true;
  const inputCls =
    'w-full rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 ' +
    'text-gray-800 dark:text-gray-200 px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500';
  // 🔀 A THREE-BUTTON dialog keeps its buttons on ONE row (owner, 2026-09-28,
  // on the collapse prompt: *""Save and close" is on its own line which look
  // wrong"*). `max-w-md` leaves 408px of row and the three labels need ~416,
  // so the third wrapped. With `altLabel` the panel widens to `max-w-lg` and
  // the row stops wrapping from `sm` up; below `sm` (a phone) it still wraps,
  // which is the honest answer on a 360px screen. 🔑 Keyed on `hasAlt` ONLY:
  // a 1/2-button dialog renders the pre-2026-09-28 class strings byte for
  // byte (pinned by `ConfirmDialog.test.js`). The one-row claim is a LAYOUT
  // claim — measured in a real browser, not jsdom (see the test file).
  const panelWidthCls = hasAlt ? 'max-w-lg' : 'max-w-md';
  // 📱 With short mobile labels the row never wraps (2026-10-01: three short
  // labels fit one row at 360px — measured in Chromium at 360/375/412).
  // 📱 Below `md` every button is a 44px tap target (house floor; they measured 38px — known gap
  // closed 2026-10-30). Set on the ROW so each button keeps its exact `BTN` class (callers and
  // goldens pin those); height only, phones only — the desktop dialog is unchanged.
  const PHONE_TAP = ' max-md:[&>button]:min-h-[44px]';
  const buttonRowCls = (hasMobileLabels
    ? 'flex flex-nowrap whitespace-nowrap justify-end gap-2'
    : hasAlt
      ? 'flex flex-wrap sm:flex-nowrap sm:whitespace-nowrap justify-end gap-2'
      : 'flex flex-wrap justify-end gap-2') + PHONE_TAP;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4 ${MODAL_OVERLAY}`}
      onMouseDown={(e) => { if (e.target === e.currentTarget && !busy) onCancel?.(); }}
      role="dialog"
      aria-modal="true"
      aria-labelledby={title ? titleId : undefined}
      aria-describedby={message != null ? descId : undefined}
      aria-label={title ? undefined : 'Confirm'}
    >
      <div ref={panelRef} className={`bg-white dark:bg-gray-800 rounded-lg shadow-xl w-full ${panelWidthCls} p-5 ${MODAL_PANEL_MOBILE_CAP}`}>
        {title && (
          <h2 id={titleId} className={`text-lg font-semibold mb-2 ${isDanger ? 'text-red-600 dark:text-red-400' : 'text-gray-900 dark:text-gray-100'}`}>
            {title}
          </h2>
        )}
        {message != null && (
          <div id={descId} className="text-sm text-gray-600 dark:text-gray-300 mb-4">{message}</div>
        )}

        {hasRequireText && (
          <div className="mb-4">
            <label className={FIELD_LABEL_CLASS}>
              Type <strong className="text-gray-800 dark:text-gray-200">{requireText}</strong> to confirm:
            </label>
            <input
              ref={requireRef}
              type="text"
              value={typed}
              onChange={(e) => setTyped(e.target.value)}
              disabled={busy}
              placeholder={requireText}
              className={inputCls}
            />
          </div>
        )}

        {hasPrompt && (
          <div className="mb-4">
            <label className={FIELD_LABEL_CLASS}>{promptLabel}</label>
            <textarea
              ref={promptRef}
              value={promptValue}
              onChange={(e) => onPromptChange?.(e.target.value)}
              disabled={busy}
              maxLength={promptMaxLength}
              rows={3}
              className={`${inputCls} resize-y`}
            />
          </div>
        )}

        {/* The body slot. Rendered only when there is something to render, so
            an omitted slot leaves no wrapper and eats no margin — the
            `ui/PageHeaderBand` rule about empty flex children. */}
        {hasBody && <div ref={bodyRef} className="mb-4">{children}</div>}

        <div className={buttonRowCls}>
          {!hideCancel && (
            <button ref={cancelRef} type="button" onClick={() => onCancel?.()} disabled={busy} className={rowBtnCls(BTN.secondary)}>
              {cancelLabel}
            </button>
          )}
          {hasAlt && (
            // Between cancel and confirm — see the ORDER note in the docblock.
            // `danger-soft` rather than `danger`: it must LOOK destructive
            // without competing with the primary for the cursor.
            <button
              type="button"
              onClick={() => onAlt?.()}
              disabled={busy}
              className={rowBtnCls(BTN[altVariant])}
              data-testid="confirm-dialog-alt"
            >
              {shownAltLabel}
            </button>
          )}
          <button
            ref={confirmRef}
            type="button"
            onClick={() => onConfirm?.()}
            disabled={isConfirmDisabled}
            className={rowBtnCls(confirmVariant ? BTN[confirmVariant] : isDanger ? BTN.danger : variant === 'save' ? BTN.confirm : BTN.primary)}
          >
            {busy ? 'Working…' : shownConfirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
